import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const PORTFOLIO = [
  {
    id: 'luxe-mane',
    name: 'Luxe Mane Studio',
    tier: 'Starter',
    tag: 'Hair extension specialist, NYC',
    href: '/portfolio/starter',
    image: '/images/live/luxemane.png',
    palette: { bg: '#E8DFCD', accent: '#A8794D', shape: '#3F3026' },
  },
  {
    id: 'harbor-therapy',
    name: 'Harbor Therapy',
    tier: 'Practice',
    tag: 'Group therapy practice, Brooklyn',
    href: '/portfolio/practice',
    image: '/images/live/harbortherapy.png',
    palette: { bg: '#D6DDD2', accent: '#4A6B5C', shape: '#283A2F' },
  },
  {
    id: 'brewed',
    name: 'Brewed',
    tier: 'Commerce',
    tag: 'Specialty coffee, Manhattan',
    href: '/portfolio/commerce',
    image: '/images/live/brewed.png',
    palette: { bg: '#2B1F14', accent: '#C8864A', shape: '#F5ECD7' },
  },
];

// 10 total steps: name, email, then 8 scoring questions.
const QUIZ_STEPS = [
  {
    id: 'name',
    prompt: "What's your name?",
    type: 'text',
    placeholder: 'Your first name',
    sideLabel: 'Your name',
  },
  {
    id: 'email',
    prompt: "And your email?",
    type: 'email',
    placeholder: 'you@example.com',
    sideLabel: 'Your email',
  },
  {
    id: 'q1',
    prompt: 'What best describes your business right now?',
    sideLabel: 'About your business',
    options: [
      { value: 'solo',     label: "I'm a solo service provider (one offer, one person)",     score: { starter: 2 } },
      { value: 'team',     label: 'I run a small team or practice with multiple providers',   score: { practice: 2 } },
      { value: 'products', label: 'I sell physical or digital products online',               score: { ecommerce: 3 } },
      { value: 'growing',  label: "I have a service business that's growing and needs structure", score: { business: 2 } },
    ],
  },
  {
    id: 'q2',
    prompt: 'How do you want people to reach you or buy from you?',
    sideLabel: 'How clients reach you',
    options: [
      { value: 'contact',   label: 'Just a contact form or my info',               score: { starter: 2 } },
      { value: 'inquiry',   label: 'An inquiry form with structured intake',        score: { business: 2 } },
      { value: 'booking',   label: 'Booking with staff selection and availability', score: { practice: 2 } },
      { value: 'checkout',  label: 'A full cart and checkout experience',           score: { ecommerce: 3 } },
    ],
  },
  {
    id: 'q3',
    prompt: "How many pages do you think you'll need?",
    sideLabel: 'Pages you need',
    options: [
      { value: 'one',      label: 'Just one page',                                  score: { starter: 2 } },
      { value: 'standard', label: 'Around 4 to 6 pages',                            score: { business: 2 } },
      { value: 'many',     label: 'More than 6, including staff profiles',          score: { practice: 2 } },
      { value: 'shop',     label: 'Product pages, categories, shop structure',      score: { ecommerce: 3 } },
    ],
  },
  {
    id: 'q4',
    prompt: 'Do you need to take payments through the site?',
    sideLabel: 'Payments',
    options: [
      { value: 'no',        label: 'No, I invoice or collect payment separately',     score: { starter: 1, business: 1 } },
      { value: 'optional',  label: 'Maybe, like deposits or session fees',            score: { practice: 2 } },
      { value: 'yes',       label: 'Yes, full product checkout with cart and orders', score: { ecommerce: 3 } },
    ],
  },
  {
    id: 'q5',
    prompt: 'Do you have multiple service providers or staff that need to be bookable independently?',
    sideLabel: 'Multiple providers',
    options: [
      { value: 'solo',   label: "No, it's just me",                        score: { starter: 1, business: 1 } },
      { value: 'team',   label: 'Yes, 2 or more providers or staff',       score: { practice: 3 } },
      { value: 'unsure', label: 'Not yet, but we might grow into it',      score: { practice: 1, business: 1 } },
    ],
  },
  {
    id: 'q6',
    prompt: 'How much content do you have ready to hand over?',
    sideLabel: 'Content readiness',
    options: [
      { value: 'ready',    label: 'Mostly ready: copy, photos, brand sorted',           score: { starter: 1, business: 1 } },
      { value: 'partial',  label: 'Some of it, with a few gaps to fill',                score: { business: 1, practice: 1 } },
      { value: 'starting', label: 'Starting from scratch, will need guidance',          score: { business: 1, ecommerce: 1 } },
      { value: 'products', label: 'I have product data, photos, and descriptions ready', score: { ecommerce: 2 } },
    ],
  },
  {
    id: 'q7',
    prompt: "What's your biggest frustration with your current online presence?",
    sideLabel: 'Biggest frustration',
    options: [
      { value: 'none',       label: "I don't have one yet, starting from zero",       score: { starter: 2 } },
      { value: 'looks',      label: "It looks outdated or doesn't reflect my brand",  score: { business: 2 } },
      { value: 'leads',      label: "It doesn't bring in leads or convert visitors",  score: { business: 1, practice: 1 } },
      { value: 'operations', label: "Managing bookings, staff, or clients is a mess", score: { practice: 2 } },
      { value: 'selling',    label: "I can't sell products effectively online",       score: { ecommerce: 3 } },
    ],
  },
  {
    id: 'q8',
    prompt: 'Where do you see this site in 2 years?',
    sideLabel: 'Two-year outlook',
    options: [
      { value: 'same',      label: 'About the same, a clean, reliable foundation',   score: { starter: 1, business: 1 } },
      { value: 'growing',   label: 'Adding pages, features, maybe a blog',           score: { business: 1, practice: 1 } },
      { value: 'expanding', label: 'Growing team, more services, or more locations', score: { practice: 2 } },
      { value: 'scaling',   label: 'More products, more sales, scaling the shop',    score: { ecommerce: 3 } },
    ],
  },
];

const SCORING_STEPS = QUIZ_STEPS.filter((s) => s.options);

const TIER_NAMES = {
  starter: 'Starter',
  business: 'Business',
  practice: 'Practice',
  ecommerce: 'Commerce',
};

const TIER_PRICES = {
  starter:   { from: '$1,499', note: 'one page, one offer' },
  business:  { from: '$2,499', note: 'multi-page, structured inquiry' },
  practice:  { from: '$3,999', note: 'booking, intake, staff' },
  ecommerce: { from: '$4,499', note: 'cart, checkout, orders' },
};

const TIER_TAGLINES = {
  starter:   'A one-page site for solo providers with a single, clear offer.',
  business:  'A multi-page site with the structure and inquiry flow a growing business needs.',
  practice:  'A multi-staff service site with custom booking, intake, and staff profiles.',
  ecommerce: 'A full e-commerce store with product pages, cart, Stripe checkout, and order flow.',
};

const TIER_ORDER = ['starter', 'business', 'practice', 'ecommerce'];

function computeRecommendation(answers) {
  const scores = { starter: 0, business: 0, practice: 0, ecommerce: 0 };
  SCORING_STEPS.forEach((q) => {
    const value = answers[q.id];
    if (!value) return;
    const opt = q.options.find((o) => o.value === value);
    if (!opt) return;
    Object.entries(opt.score).forEach(([t, p]) => {
      scores[t] = (scores[t] || 0) + p;
    });
  });
  const sorted = Object.entries(scores).sort(([, a], [, b]) => b - a);
  const topScore = sorted[0][1];
  const tied = sorted.filter(([, s]) => s === topScore).map(([t]) => t);
  return TIER_ORDER.find((t) => tied.includes(t));
}

async function submitQuizToFormspree(answers, recommendedTier) {
  const labelMap = {};
  SCORING_STEPS.forEach((q) => {
    const val = answers[q.id];
    if (!val) return;
    const opt = q.options.find((o) => o.value === val);
    labelMap[q.prompt] = opt ? opt.label : val;
  });

  const body = {
    _subject: `Quiz completed: ${answers.name || 'Anonymous'} -> ${recommendedTier}`,
    name: answers.name || '',
    email: answers.email || '',
    recommended_tier: recommendedTier,
    source: 'Homepage Quiz',
    ...labelMap,
  };

  try {
    await fetch('https://formspree.io/f/mqewrgkd', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    // silent
  }
}

// Cursor glow hook
function useCursorGlow() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = null;
    let nx = 50, ny = 50;
    const apply = () => {
      el.style.setProperty('--mx', nx + '%');
      el.style.setProperty('--my', ny + '%');
      raf = null;
    };
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      nx = ((e.clientX - r.left) / r.width) * 100;
      ny = ((e.clientY - r.top) / r.height) * 100;
      if (raf === null) raf = requestAnimationFrame(apply);
    };
    const onLeave = () => {
      nx = 50; ny = 50;
      if (raf === null) raf = requestAnimationFrame(apply);
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, []);
  return ref;
}

// Tilt hook
function useTilt(max = 8) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = null, tx = 0, ty = 0, gx = 50, gy = 50;
    const apply = () => {
      el.style.setProperty('--rx', ty + 'deg');
      el.style.setProperty('--ry', tx + 'deg');
      el.style.setProperty('--gx', gx + '%');
      el.style.setProperty('--gy', gy + '%');
      raf = null;
    };
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      tx = (px - 0.5) * max;
      ty = (0.5 - py) * max;
      gx = px * 100; gy = py * 100;
      if (raf === null) raf = requestAnimationFrame(apply);
    };
    const onLeave = () => {
      tx = 0; ty = 0; gx = 50; gy = 50;
      if (raf === null) raf = requestAnimationFrame(apply);
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [max]);
  return ref;
}

// Portfolio thumbnail — real screenshot, shown in full (no cropping)
function PortfolioThumb({ image, name }) {
  return (
    <img
      src={image}
      alt={`${name} live preview`}
      className="av-work-thumb-image"
    />
  );
}

function WorkCard({ item, isActive, onMouseEnter }) {
  const tiltRef = useTilt(5);
  return (
    <Link
      to={item.href}
      className={'av-work-card ' + (isActive ? 'is-active' : '')}
      ref={tiltRef}
      onMouseEnter={onMouseEnter}
      style={{ '--accent': item.palette.accent }}
    >
      <div className="av-work-meta-top">
        <span className="av-work-tier">{item.tier}</span>
      </div>
      <div className="av-work-frame">
        <PortfolioThumb image={item.image} name={item.name} />
        <div className="av-work-shine" aria-hidden />
      </div>
      <div className="av-work-info">
        <h3>{item.name}</h3>
        <p>{item.tag}</p>
      </div>
      <div className="av-work-cta">
        <span>View preview</span><span aria-hidden>↗</span>
      </div>
    </Link>
  );
}

function ArchHero() {
  const ref = useCursorGlow();
  return (
    <section className="av-hero" ref={ref}>
      <div className="av-hero-grid" aria-hidden />
      <div className="av-hero-float av-float-browser" aria-hidden>
        <div className="av-float-browser-bar">
          <span /><span /><span />
          <div className="av-float-browser-url">luxe-mane.studio</div>
        </div>
        <div className="av-float-browser-body">
          <div className="av-float-browser-h" />
          <div className="av-float-browser-h short" />
          <div className="av-float-browser-img" />
          <div className="av-float-browser-row">
            <div className="av-float-browser-cell" />
            <div className="av-float-browser-cell" />
          </div>
        </div>
      </div>
      <div className="av-hero-float av-float-tag" aria-hidden>
        <span className="av-float-tag-dot" />
        <span>From $1,499 · 3 week build</span>
      </div>
      <div className="av-hero-float av-float-cursor" aria-hidden>
        <svg viewBox="0 0 24 24" width="22" height="22">
          <path d="M3 2 L3 18 L8 14 L11 21 L14 19 L11 13 L18 13 Z" fill="currentColor" />
        </svg>
        <span className="av-float-cursor-label">you</span>
      </div>
      <div className="av-hero-float av-float-code" aria-hidden>
        <span className="av-float-code-line"><span className="k">const</span> <span className="v">site</span> = <span className="s">yours</span>;</span>
        <span className="av-float-code-line"><span className="k">export</span> {'{ '}<span className="v">code</span>, <span className="v">domain</span>, <span className="v">keys</span>{' }'};</span>
      </div>
      <div className="av-hero-glow" aria-hidden />

      <div className="av-container av-hero-inner">
        <p className="av-hero-kicker av-fade av-fade-1">
          <span className="av-hero-kicker-rule" />
          For solo providers, growing practices, and shops
        </p>
        <h1 className="av-fade av-fade-2">
          <span className="av-h1-line">Websites that <em>grow</em></span>
          <span className="av-h1-line">your business, not just</span>
          <span className="av-h1-line">your <em>online presence.</em></span>
        </h1>
        <div className="av-hero-cols av-fade av-fade-3">
          <div className="av-hero-col">
            <span className="av-label">Approach</span>
            <p>Every site is custom built to bring in inquiries, bookings, or sales, not just to look good. You own the code, the domain, and the hosting, with no platform rent and nothing to get locked out of. During the build you get a progress update every week or two, so you always know where things stand.</p>
          </div>
          <div className="av-hero-col">
            <span className="av-label">Copywriting</span>
            <p>The words are written for you, not left to you. Most of a site is its copy, and most designers hand over an empty layout for you to fill. Here every page is custom written as part of the build, tailored to your business and your voice, structured to turn visitors into inquiries.</p>
          </div>
          <div className="av-hero-col">
            <span className="av-label">Outcome</span>
            <p>A site that loads fast, ranks well in search, and is ready to grow with you. Not a brochure with a domain name attached, and not a template or AI-generated site that looks like a hundred others. Built and written specifically for your business, so it performs, and looks like nothing else in your market.</p>
          </div>
        </div>
        <div className="av-actions av-fade av-fade-4">
          <a className="av-btn av-btn-primary" href="#quiz"><span>Find your tier</span><span aria-hidden>→</span></a>
          <a className="av-btn av-btn-ghost" href="#work"><span>See selected work</span></a>
        </div>
      </div>
    </section>
  );
}

function ArchWork() {
  const [active, setActive] = useState(0);
  return (
    <section className="av-work" id="work">
      <div className="av-container">
        <div className="av-section-head">
          <div>
            <span className="av-section-tag">Selected work</span>
            <h2>Selected work.<br /><em>Three live previews.</em></h2>
          </div>
          <p className="av-section-blurb">Each preview is a working site you can navigate end to end. Click through, copy a URL, inspect the source. The sites we ship behave exactly like these.</p>
        </div>
        <div className="av-work-grid">
          {PORTFOLIO.map((item, i) => (
            <WorkCard
              key={item.id}
              item={item}
              isActive={active === i}
              onMouseEnter={() => setActive(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ArchQuiz() {
  const [answers, setAnswers] = useState({});
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [direction, setDirection] = useState('forward');

  const total = QUIZ_STEPS.length;
  const allDone = step >= total;
  const rec = allDone ? computeRecommendation(answers) : null;
  const current = !allDone ? QUIZ_STEPS[step] : null;

  const goNext = (key, value) => {
    setDirection('forward');
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step === 0) return;
    setDirection('back');
    setStep((s) => s - 1);
  };

  const handleOption = (qid, value) => goNext(qid, value);

  const handleTextSubmit = () => {
    if (!current) return;
    const value = current.id === 'email' ? emailInput.trim() : nameInput.trim();
    if (!value) return;
    goNext(current.id, value);
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setNameInput('');
    setEmailInput('');
    setSubmitted(false);
    setDirection('forward');
  };

  useEffect(() => {
    if (allDone && !submitted) {
      submitQuizToFormspree(answers, rec);
      setSubmitted(true);
    }
  }, [allDone, submitted, answers, rec]);

  // Keyboard navigation: arrows + number keys
  useEffect(() => {
    if (!open || allDone) return;
    const handler = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea';

      if (e.key === 'ArrowLeft' && !isTyping) {
        e.preventDefault();
        goBack();
        return;
      }
      if (!current || !current.options) return;
      const num = parseInt(e.key, 10);
      if (!isNaN(num) && num >= 1 && num <= current.options.length && !isTyping) {
        e.preventDefault();
        const opt = current.options[num - 1];
        handleOption(current.id, opt.value);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allDone, step, current]);

  return (
    <section className="av-quiz" id="quiz">
      <div className="av-container">
        <div className="av-section-head">
          <div>
            <span className="av-section-tag tone-warm">Find your match</span>
            <h2>Tell us what you need.<br /><em>We'll point to the tier.</em></h2>
          </div>
          <p className="av-section-blurb">A short walk through your business, what you sell, and how you want clients to reach you. We tell you which package fits and why.</p>
        </div>

        {!open && (
          <div className="av-quiz-invite">
            <div className="av-quiz-invite-grid" aria-hidden />
            <div className="av-quiz-invite-inner">
              <span className="av-label">The matchmaker</span>
              <h3>Ten questions, <em>one clear answer.</em></h3>
              <p>About a minute. We use your answers to recommend a tier and explain why it fits.</p>
              <div className="av-quiz-invite-meta">
                <div><span className="av-quiz-invite-n">10</span><span>questions</span></div>
                <div><span className="av-quiz-invite-n">~60s</span><span>to finish</span></div>
                <div><span className="av-quiz-invite-n">04</span><span>possible tiers</span></div>
              </div>
              <button className="av-btn av-btn-primary av-quiz-invite-cta" onClick={() => setOpen(true)}>
                <span>Start the matchmaker</span><span aria-hidden>→</span>
              </button>
            </div>
            <div className="av-quiz-invite-stack" aria-hidden>
              <div className="av-quiz-invite-card c1"><span>01</span><small>Your name</small></div>
              <div className="av-quiz-invite-card c2"><span>02</span><small>Your email</small></div>
              <div className="av-quiz-invite-card c3"><span>03</span><small>About your business</small></div>
            </div>
          </div>
        )}

        {open && !allDone && current && (
          <div className="av-quiz-stage">
            <div className="av-quiz-stage-top">
              <span className="av-quiz-count">
                {String(step + 1).padStart(2, '0')} <span>/ {String(total).padStart(2, '0')}</span>
              </span>
              <span className="av-quiz-side-label">{current.sideLabel}</span>
            </div>

            <div className="av-quiz-track">
              <div key={step} className={'av-quiz-slide is-' + direction}>
                <p className="av-quiz-prompt">
                  {current.id === 'email' && answers.name
                    ? `Nice to meet you, ${answers.name}. What's your email?`
                    : current.prompt}
                </p>

                {(current.type === 'text' || current.type === 'email') ? (
                  <div className="av-quiz-text-input">
                    <input
                      type={current.type}
                      value={current.id === 'email' ? emailInput : nameInput}
                      onChange={(e) =>
                        current.id === 'email'
                          ? setEmailInput(e.target.value)
                          : setNameInput(e.target.value)
                      }
                      onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                      placeholder={current.placeholder}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="av-btn av-btn-primary"
                      onClick={handleTextSubmit}
                      disabled={!(current.id === 'email' ? emailInput.trim() : nameInput.trim())}
                    >
                      <span>Continue</span><span aria-hidden>→</span>
                    </button>
                  </div>
                ) : (
                  <div className="av-quiz-opts">
                    {current.options.map((opt, oi) => (
                      <button
                        key={opt.value}
                        className={'av-quiz-opt ' + (answers[current.id] === opt.value ? 'is-on' : '')}
                        onClick={() => handleOption(current.id, opt.value)}
                      >
                        <span className="av-quiz-opt-key">{oi + 1}</span>
                        <span className="av-quiz-opt-label">{opt.label}</span>
                        <span className="av-quiz-opt-arr" aria-hidden>→</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="av-quiz-nav">
              <button
                type="button"
                className="av-quiz-nav-btn"
                onClick={goBack}
                disabled={step === 0}
              >
                <span aria-hidden>←</span><span>Previous</span>
              </button>
              <span className="av-quiz-nav-hint">Use arrow keys or 1–4 to answer</span>
              <button
                type="button"
                className="av-quiz-nav-btn av-quiz-nav-btn-restart"
                onClick={restart}
              >
                <span>Reset</span>
              </button>
            </div>
          </div>
        )}

        {open && allDone && (
          <div className="av-quiz-result">
            <span className="av-label">
              {answers.name ? `${answers.name}, your match is` : 'Your match'}
            </span>
            <h3>{TIER_NAMES[rec]}</h3>
            <div className="av-quiz-result-price">
              <span className="av-label">From</span>
              <strong>{TIER_PRICES[rec].from}</strong>
              <span className="av-quiz-result-note">{TIER_PRICES[rec].note}</span>
            </div>
            <p className="av-quiz-result-blurb">{TIER_TAGLINES[rec]}</p>
            <div className="av-actions">
              <Link to={`/services#${rec}`} className="av-btn av-btn-primary">
                <span>Open the {TIER_NAMES[rec]} tier</span><span aria-hidden>→</span>
              </Link>
              <Link to="/contact" className="av-btn av-btn-ghost">
                <span>Get in touch</span>
              </Link>
            </div>
            <button className="av-quiz-restart" onClick={restart}>← Reset answers</button>
          </div>
        )}
      </div>
    </section>
  );
}

function ArchCTA() {
  const ref = useCursorGlow();
  return (
    <section className="av-cta" id="contact" ref={ref}>
      <div className="av-cta-glow" aria-hidden />
      <div className="av-cta-grid" aria-hidden />

      <div className="av-container av-cta-inner">
        <div className="av-cta-head">
          <div className="av-cta-eyebrow">
            <span className="av-cta-eyebrow-dot" />
            <span>Currently accepting projects</span>
          </div>
          <h2>
            <span>Have a project</span>
            <span className="av-cta-h-em"><em>in mind?</em><span className="av-cta-h-mark" aria-hidden>↘</span></span>
          </h2>
          <p className="av-cta-blurb">Tell us what you're building. We follow up with ideas, a rough timeline, and pricing within two business days.</p>
        </div>

        <div className="av-cta-body">
          <Link to="/contact" className="av-cta-card" style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
            <div className="av-cta-card-head">
              <div className="av-cta-card-dots"><span /><span /><span /></div>
              <span className="av-cta-card-title">New inquiry</span>
              <span className="av-cta-card-meta">click to open</span>
            </div>
            <div className="av-cta-card-body">
              <div className="av-cta-field">
                <span>From</span>
                <div style={{ borderBottom: '1px solid var(--av-line)', padding: '8px 0', fontStyle: 'italic', color: 'var(--av-ink-3)' }}>you@yourbusiness.com</div>
              </div>
              <div className="av-cta-field">
                <span>What you're building</span>
                <div style={{ borderBottom: '1px solid var(--av-line)', padding: '8px 0', fontStyle: 'italic', color: 'var(--av-ink-3)' }}>e.g. a six-page site for a therapy practice</div>
              </div>
              <div className="av-cta-field av-cta-field-area">
                <span>The short version</span>
                <div style={{ padding: '10px 0', minHeight: '60px', fontStyle: 'italic', color: 'var(--av-ink-3)' }}>A sentence or two about your business, your timeline, and what you have today.</div>
              </div>
              <div className="av-cta-card-foot">
                <div className="av-cta-card-chips">
                  <span className="av-cta-chip">Starter</span>
                  <span className="av-cta-chip">Business</span>
                  <span className="av-cta-chip is-on">Practice</span>
                  <span className="av-cta-chip">Commerce</span>
                  <span className="av-cta-chip-aux">Not sure yet?</span>
                </div>
                <span className="av-btn av-btn-primary-light av-cta-send"><span>Send inquiry</span><span aria-hidden>→</span></span>
              </div>
            </div>
          </Link>

          <aside className="av-cta-aside">
            <div className="av-cta-next">
              <span className="av-label av-label-light">What happens next</span>
              <ol>
                <li>
                  <span className="av-cta-step-n">01</span>
                  <span><strong>You send a note.</strong> One paragraph is plenty.</span>
                </li>
                <li>
                  <span className="av-cta-step-n">02</span>
                  <span><strong>We reply within two business days</strong> with thoughts, a timeline, and a price.</span>
                </li>
                <li>
                  <span className="av-cta-step-n">03</span>
                  <span><strong>If it fits, we kick off.</strong> If not, we point you somewhere that does.</span>
                </li>
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Home() {
  return (
    <div className="home">
      <ArchHero />
      <ArchWork />
      <ArchQuiz />
      <ArchCTA />
    </div>
  );
}

export default Home;